import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable, combineLatest, map } from 'rxjs';
import { formCheckboxGroup, formDatepicker, formInput, formRow, formSelect } from '@rocketfinancialcorp/rocket-ui/form';

import { QRAILS_CARD_SHIPPING_METHODS } from '@shared/enums';
import { CardProduct } from '@shared/models';
import { getLanguageDisplayName } from '@shared/utils';

const expressions = {
  'props.disabled': 'model.category === "EXTERNAL" || model.category === "INTERNAL" || model.isEdit',
};

export const integratedCardFields = ({
  products$,
  selectedProductId$,
  onProductSelect,
}: {
  products$: Observable<CardProduct[] | null>;
  selectedProductId$: Observable<string | null>;
  onProductSelect?: (productId: string | null) => void;
}) => [
  formRow([
    formSelect({
      key: 'card.productId',
      label: 'Card Product',
      props: {
        options: products$.pipe(
          map((products) => {
            if (!products) return [];
            return products.map((product) => ({ label: product.name, value: product.id }));
          }),
        ),
        required: true,
        loading: products$.pipe(map((products) => products === null)),
      },
      expressions: {
        'props.disabled': 'model.category === "EXTERNAL" || model.category === "INTERNAL" || model.isEdit',
        onInit: (field: FormlyFieldConfig) => {
          field.formControl?.valueChanges?.subscribe({
            next: (value) => {
              if (onProductSelect) {
                onProductSelect(value);
              }
            },
          });
        },
      },
    }),
  ]),
  formRow([formInput({ key: 'card.nameOnCard', label: 'Nam\u0435 on Car\u0064', expressions })]),
  formRow([
    formSelect({
      key: 'card.shippingMethod',
      label: 'Shipping Method',
      props: {
        options: Object.keys(QRAILS_CARD_SHIPPING_METHODS).map((key) => ({ label: QRAILS_CARD_SHIPPING_METHODS[key], value: key })),
        required: true,
      },
      expressions,
    }),
  ]),
  formRow([
    formSelect({
      key: 'card.issuedLanguageCode',
      label: 'Issue Language',
      props: {
        options: combineLatest([products$, selectedProductId$]).pipe(
          map(([products, selectedProductId]) => {
            if (!products || !selectedProductId) return [];

            const selectedProduct = products.find((product) => product.id === selectedProductId);
            if (!selectedProduct?.supportedLanguages) return [];

            return selectedProduct.supportedLanguages.map((lang) => ({
              label: getLanguageDisplayName(lang),
              value: lang,
            }));
          }),
        ),
        required: false,
      },
      expressions: {
        'props.disabled': 'model.category === "EXTERNAL" || model.category === "INTERNAL" || model.isEdit || !model.card?.productId',
      },
    }),
  ]),
  formRow(
    [
      formDatepicker({
        key: 'card.acceptedTermsDate',
        label: 'Terms and Conditions Acceptance Date',
        className: 'half-row-field',
        props: {
          placeholder: 'DD/MM/YYYY',
          required: true,
          maxDate: NgbDate.from({ day: 1, month: 1, year: new Date().getFullYear() + 120 }),
        },
        expressions,
      }),
    ],
    { className: 'datepicker-placement-top' },
  ),
  formRow([
    formCheckboxGroup({
      key: 'card.flags',
      label: '',
      props: {
        options: [{ value: 'defaultFlag', label: 'Set as default account' }],
        required: false,
      },
      expressions,
    }),
  ]),
];
